import { useState } from "react";
import { useNavigate } from "react-router-dom";
import MobileLayout from "@/components/MobileLayout";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Lock, ArrowLeft } from "lucide-react";

export default function AccountPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    repeat_password: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    if (form.new_password.length < 8) {
      setErr("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }

    if (form.new_password !== form.repeat_password) {
      setErr("Las contraseñas nuevas no coinciden.");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setForm({ current_password: "", new_password: "", repeat_password: "" });
      toast.success("Contraseña cambiada correctamente");
    } catch (e2) {
      setErr(formatApiError(e2?.response?.data?.detail) || "No se pudo cambiar la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MobileLayout>
      <button
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-1 text-sm font-bold text-slate-600"
      >
        <ArrowLeft className="h-4 w-4" /> Volver
      </button>

      <div className="mb-6 rounded-3xl border-2 border-slate-100 bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center gap-3">
          <div className="rounded-2xl bg-[#4d7c0f]/10 p-3 text-[#4d7c0f]">
            <User className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-slate-900">Mi cuenta</h1>
            <p className="text-sm text-slate-500">{user?.email}</p>
          </div>
        </div>
        <p className="text-base text-slate-600">
          Gestiona los datos básicos de acceso a la app KAPEI.
        </p>
      </div>

      <form onSubmit={onSubmit} className="rounded-3xl border-2 border-slate-100 bg-white p-5 shadow-sm">
        <div className="mb-5 flex items-center gap-2">
          <Lock className="h-5 w-5 text-[#4d7c0f]" />
          <h2 className="text-xl font-black text-slate-900">Cambiar contraseña</h2>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-sm font-bold uppercase tracking-widest text-slate-700">
              Contraseña actual
            </Label>
            <PasswordInput
              required
              value={form.current_password}
              onChange={(e) => set("current_password", e.target.value)}
              className="mt-2 h-13 rounded-xl border-2 border-slate-300 px-4 text-lg"
            />
          </div>

          <div>
            <Label className="text-sm font-bold uppercase tracking-widest text-slate-700">
              Nueva contraseña
            </Label>
            <PasswordInput
              required
              minLength={8}
              value={form.new_password}
              onChange={(e) => set("new_password", e.target.value)}
              className="mt-2 h-13 rounded-xl border-2 border-slate-300 px-4 text-lg"
              placeholder="Mínimo 8 caracteres"
            />
          </div>

          <div>
            <Label className="text-sm font-bold uppercase tracking-widest text-slate-700">
              Repetir nueva contraseña
            </Label>
            <PasswordInput
              required
              minLength={8}
              value={form.repeat_password}
              onChange={(e) => set("repeat_password", e.target.value)}
              className="mt-2 h-13 rounded-xl border-2 border-slate-300 px-4 text-lg"
            />
          </div>

          {err && (
            <div className="rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700">
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="kapei-btn-huge w-full bg-[#4d7c0f] text-white hover:bg-[#3f6212]"
          >
            {loading ? "Guardando…" : "Guardar nueva contraseña"}
          </Button>
        </div>
      </form>
    </MobileLayout>
  );
}
